import React, { memo, useEffect, useRef } from 'react';
import { JsonForms } from '@jsonforms/react';
import { ThemeProvider } from '@mui/material';
import {
  materialCells,
  materialRenderers,
} from '@jsonforms/material-renderers';

import CountryGreetingControl from './Controls/CountryGreetingControl';
import {
  countryGreetingTester,
  staticWidgetDescriptionMessageTester,
  staticWidgetLoaderMessageTester,
} from './Controls/countryGreetingTester';
import { jsonTextAreaTester } from './Controls/JsonTeaxtAreaControl/jsonTextAreaTester';
import JsonTextAreaControl from './Controls/JsonTeaxtAreaControl/JsonTeaxtAreaControl';
import AdditionalVocabControl from './Controls/AdditionalVocabEditorControl/AdditionalVocabControl';
import UrlFileUploadControl from './Controls/UrlFileUploadControl';
import { theme } from './theme';
import type { FormJsonData } from '../../api/form-controller';
import { additionalVocabTester } from './Controls/JsonTeaxtAreaControl/additionalVocabTester';
import { urlFileUploadTester } from './Controls/urlFileUploadTester';

const renderers = [
  ...materialRenderers,
  { tester: countryGreetingTester, renderer: CountryGreetingControl },
  {
    tester: staticWidgetDescriptionMessageTester,
    renderer: CountryGreetingControl,
  },
  {
    tester: staticWidgetLoaderMessageTester,
    renderer: CountryGreetingControl,
  },
  {
    tester: jsonTextAreaTester,
    renderer: JsonTextAreaControl,
  },
  {
    tester: additionalVocabTester,
    renderer: AdditionalVocabControl,
  },
  {
    tester: urlFileUploadTester,
    renderer: UrlFileUploadControl,
  },
];

export type JsonBuilderProps = {
  onChange: (data: any) => void;
  formData: any;
  schemaData: FormJsonData;
  onChangeUpload?: (file: File) => void;
};

const JsonFormBuilder: React.FC<JsonBuilderProps> = ({
  onChange,
  formData,
  schemaData,
  onChangeUpload,
}) => {
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (window as any).__jsonFormsAdditionalProps = {
      onChangeUpload,
    };

    return () => {
      delete (window as any).__jsonFormsAdditionalProps;
    };
  }, [onChangeUpload]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (
        document.activeElement instanceof HTMLInputElement &&
        document.activeElement.type === 'number'
      ) {
        event.preventDefault();
      }
    };

    const inputElements = formRef.current?.querySelectorAll(
      'input[type="number"]'
    );

    inputElements?.forEach(input => {
      (input as HTMLInputElement).addEventListener(
        'wheel',
        handleWheel as EventListener
      );
    });

    return () => {
      inputElements?.forEach(input => {
        (input as HTMLInputElement).removeEventListener(
          'wheel',
          handleWheel as EventListener
        );
      });
    };
  }, []);

  return (
    <div className="flex flex-col items-center py-6 px-2" ref={formRef}>
      <ThemeProvider theme={theme}>
        <JsonForms
          schema={(schemaData as any)?.jsonSchema}
          uischema={(schemaData as any)?.uiSchema}
          data={formData}
          renderers={renderers}
          cells={materialCells}
          validationMode="ValidateAndShow"
          onChange={onChange}
          config={{
            material: {
              variant: 'outlined',
              table: { disableHeader: false },
              popper: { disablePortal: false },
            },
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export default memo(JsonFormBuilder);
